import React from "react";
import { GameMessageEvent, GameState } from "../../../core/protocol/types";
import { MESSAGE_HANDLERS } from "./messageAnimations";

/**
 * GameMessage component - Dispatcher for game messages
 * 
 * This component looks up the appropriate animation handler based on the message type
 * and delegates rendering to that handler. This architecture allows each message type
 * to define its own animation and styling behavior.
 * 
 * To add a new message type:
 * 1. Add the type to GameMessageEvent in types.ts
 * 2. Create a new animation component
 * 3. Register it in MESSAGE_HANDLERS in messageAnimations.tsx
 */
interface GameMessageProps {
  message: GameMessageEvent;
  localConnectionId: string;
  localTeam: number;
  gameState: GameState;
  onComplete: () => void;
}

const GameMessage: React.FC<GameMessageProps> = React.memo(({
  message,
  localConnectionId,
  localTeam,
  gameState,
  onComplete,
}) => {
  // Message rendering happens silently
  
  const handler = MESSAGE_HANDLERS[message.type];
  
  if (!handler) {
    console.warn(`[GameMessage] No handler for message type: ${message.type}`);
    return null;
  }
  
  return handler({
    message,
    localConnectionId,
    localTeam,
    gameState,
    onComplete,
  });
}, (prevProps, nextProps) => {
  // Only re-render if message ID changes (ignore onComplete since it's recreated each render)
  return prevProps.message.id === nextProps.message.id;
});

export default GameMessage;

